import type { authClient } from "@/lib/auth-client";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { queryKeys } from "@/lib/query-keys";
import type { orpc } from "@/lib/orpc";
import { Toaster } from "@papier/ui/components/ui/sonner";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { HeadContent, Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

export interface RouterAppContext {
  orpc: typeof orpc;
  queryClient: QueryClient;
  authClient: typeof authClient;
}

function isTransientAuthError(error: { status: number } | null) {
  return error !== null && (error.status === 429 || error.status >= 500);
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  context: ({ context: { authClient } }) => ({
    authOptions: {
      session: queryOptions({
        queryKey: queryKeys.session(),
        staleTime: 1000 * 60, // 1 minute
        queryFn: async () => {
          const { data: session, error } = await authClient.getSession();
          // A throttled or failing auth server must not look like a signed-out user.
          if (isTransientAuthError(error)) throw new Error("Could not verify your session");
          return session;
        },
      }),
      hasOrganizations: queryOptions({
        queryKey: queryKeys.hasOrganizations(),
        staleTime: 1000 * 60, // 1 minute
        queryFn: async () => {
          const { data: orgs, error } = await authClient.organization.list();
          if (isTransientAuthError(error)) throw new Error("Could not load your organizations");
          return !!orgs?.length;
        },
      }),
    },
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <>
      <HeadContent />
      <ImpersonationBanner />
      <Outlet />
      <TanStackDevtools
        config={{ position: "bottom-right" }}
        plugins={[
          {
            name: "TanStack Query",
            render: <ReactQueryDevtoolsPanel />,
          },
          {
            name: "TanStack Router",
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
      <Toaster richColors closeButton />
    </>
  );
}
